import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { CalendarClock, DollarSign, Loader2, Trash2, Ban } from "lucide-react";

export const fmtSP = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }); }
  catch { return iso; }
};
export const daysLeft = (iso) => {
  if (!iso) return null;
  try { return Math.ceil((new Date(iso) - Date.now()) / 86400000); } catch { return null; }
};

export function ValidityBadge({ iso, testid }) {
  const d = daysLeft(iso);
  if (d === null) return <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-secondary text-muted-foreground" data-testid={testid}>sem limite</span>;
  const cls = d <= 0 ? "bg-red-500/15 text-red-400" : d <= 7 ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400";
  return <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${cls}`} data-testid={testid}>{d <= 0 ? "expirado" : `${d}d restantes`}</span>;
}

function Info({ label, value, testid }) {
  return (
    <div className="bg-black/30 border border-border/60 rounded-sm px-2.5 py-1.5">
      <div className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-xs font-medium truncate" data-testid={testid}>{value}</div>
    </div>
  );
}

export function UserProfileModal({ open, onOpenChange, userId, onChanged, onDeleted, plans = [] }) {
  const [data, setData] = useState(null);
  const [dateVal, setDateVal] = useState("");

  useEffect(() => {
    if (!open || !userId) { setData(null); return; }
    api.get(`/admin/users/${userId}/profile`).then(({ data: d }) => setData(d)).catch(() => {
      toast.error("Falha ao carregar perfil");
      onOpenChange(false);
    });
  }, [open, userId]); // eslint-disable-line

  const u = data?.user;
  const st = data?.stats;
  const rec = data?.recursos;

  const reload = () => api.get(`/admin/users/${userId}/profile`).then(({ data: d }) => setData(d)).catch(() => {});
  const setPlan = async (plano_id) => {
    try { await api.put(`/admin/users/${userId}/plan`, { plano_id: plano_id || null }); toast.success("Plano atualizado"); reload(); }
    catch { toast.error("Falha"); }
  };
  const toggleOverride = async (chave, current) => {
    const ov = { ...(u.overrides || {}) };
    if (ov[chave] === undefined) ov[chave] = !current; // primeiro clique inverte o herdado
    else if (ov[chave] === true) ov[chave] = false;
    else delete ov[chave]; // volta a herdar do plano
    try { await api.put(`/admin/users/${userId}/overrides`, { overrides: ov }); reload(); }
    catch { toast.error("Falha"); }
  };

  const patchAccess = (access_until) => {
    setData((p) => ({ ...p, user: { ...p.user, access_until } }));
    onChanged?.(userId, access_until);
  };
  const setDate = async () => {
    if (!dateVal) return;
    try {
      const { data: r } = await api.post(`/admin/users/${userId}/access`, { access_until: dateVal });
      patchAccess(r.access_until);
      toast.success("Validade atualizada (vence 00:00 de São Paulo)");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const addDays = async (days) => {
    try {
      const { data: r } = await api.post(`/admin/users/${userId}/access`, { add_days: days });
      patchAccess(r.access_until);
      toast.success(`+${days} dias`);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const revoke = async () => {
    try {
      const { data: r } = await api.post(`/admin/users/${userId}/revoke`);
      patchAccess(r.access_until);
      toast.success("Acesso revogado");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const delUser = async () => {
    if (!window.confirm(`EXCLUIR o perfil de ${u?.email}?\n\nIsso apaga a conta, overlays, mídias, fontes e sessões. O histórico financeiro é preservado.`)) return;
    if (!window.confirm("Tem certeza? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Perfil excluído");
      onDeleted?.(userId);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao excluir"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-profile-modal">
        {!data ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-3">
                <Avatar className="h-10 w-10"><AvatarImage src={u.avatar} /><AvatarFallback>{u.name?.[0] || "U"}</AvatarFallback></Avatar>
                <span className="min-w-0">
                  <span className="flex items-center gap-2">{u.name || "—"} {u.is_admin && <span className="text-[9px] bg-primary text-primary-foreground rounded px-1">ADMIN</span>}</span>
                  <span className="block text-xs font-normal text-muted-foreground truncate" data-testid="profile-email">{u.email}</span>
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              <Info label="Criado em" value={fmtSP(u.created_at)} />
              <Info label="Último acesso" value={fmtSP(st.last_login)} />
              <Info label="Logins" value={st.logins} />
              <Info label="Status" value={u.disabled ? "Desativado" : "Ativo"} />
              <Info label="Overlays" value={st.overlays} />
              <Info label="Mídias" value={st.media} />
              <Info label="Fontes" value={st.fonts} />
              <Info label="Cota YT hoje" value={st.quota_today} />
            </div>

            {!u.is_admin && rec && (
              <div className="border border-border/60 rounded-md p-3 space-y-2" data-testid="profile-plan-section">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="text-muted-foreground">Plano:</span>
                  <select className="h-7 bg-input border border-border rounded-sm px-2 text-xs" value={u.plano_id || ""} onChange={(e) => setPlan(e.target.value)} data-testid="profile-plan-select">
                    <option value="">— nenhum —</option>
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                  </select>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${rec.ativo ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{rec.ativo ? "ativo" : "expirado/bloqueado"}</span>
                </div>
                <div className="grid grid-cols-2 gap-1" data-testid="profile-recursos">
                  {Object.entries(rec.recursos || {}).map(([chave, val]) => {
                    const overridden = (u.overrides || {})[chave] !== undefined;
                    const isBool = typeof val === "boolean";
                    return (
                      <div key={chave} className="flex items-center justify-between bg-black/20 rounded-sm px-2 py-1 text-[11px]">
                        <span className="truncate mr-1 flex items-center gap-1">{chave}{overridden && <span className="text-[8px] bg-primary/30 text-primary rounded px-1">override</span>}</span>
                        {isBool ? (
                          <button onClick={() => toggleOverride(chave, val)} className={val ? "text-emerald-500" : "text-muted-foreground/50"} data-testid={`override-${chave}`}>{val ? "✓" : "—"}</button>
                        ) : (
                          <span className="font-mono text-primary">{val}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground">Clique em ✓/— de um recurso booleano para sobrescrever manualmente (2 cliques volta a herdar do plano).</p>
              </div>
            )}

            {!u.is_admin && (
              <div className="border border-border/60 rounded-md p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <CalendarClock className="h-4 w-4 text-primary" />
                  <span className="text-muted-foreground">Válido até:</span>
                  <span className="font-mono" data-testid="profile-access-until">{fmtSP(u.access_until)}</span>
                  <ValidityBadge iso={u.access_until} testid="profile-validity-badge" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)}
                    className="h-7 bg-input border border-border rounded-sm px-2 text-xs" data-testid="profile-date-input" />
                  <Button size="sm" variant="secondary" className="h-7 rounded-sm text-[11px] px-2" onClick={setDate} data-testid="profile-date-apply">Definir data</Button>
                  <Button size="sm" variant="secondary" className="h-7 rounded-sm text-[11px] px-2" onClick={() => addDays(30)} data-testid="profile-add30">+30d</Button>
                  <Button size="sm" variant="secondary" className="h-7 rounded-sm text-[11px] px-2" onClick={() => addDays(90)}>+90d</Button>
                  <Button size="sm" variant="secondary" className="h-7 rounded-sm text-[11px] px-2" onClick={() => addDays(365)}>+1 ano</Button>
                  <Button size="sm" variant="destructive" className="h-7 rounded-sm text-[11px] px-2" onClick={revoke} data-testid="profile-revoke"><Ban className="h-3 w-3 mr-1" />Revogar</Button>
                </div>
                <p className="text-[10px] text-muted-foreground">O vencimento é sempre às 00:00 (horário de São Paulo) da data escolhida.</p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-display font-bold flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5 text-primary" /> Pagamentos</span>
                <span className="text-xs font-mono text-primary" data-testid="profile-total-pago">
                  Total: {(st.total_pago || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </span>
              </div>
              <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="profile-payments">
                {data.payments.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Nenhum pagamento registrado.</p>}
                {data.payments.map((p) => (
                  <div key={p.id} className="border border-border/60 rounded-sm px-2.5 py-1.5 text-[11px] flex justify-between gap-2">
                    <span className="font-medium">{p.moeda} {Number(p.valor).toFixed(2)} · {p.metodo} · {p.periodo_dias}d</span>
                    <span className="text-muted-foreground">{fmtSP(p.data)}</span>
                  </div>
                ))}
              </div>
            </div>

            {!u.is_admin && (
              <Button variant="destructive" className="w-full rounded-sm h-9" onClick={delUser} data-testid="profile-delete-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Excluir perfil e todos os dados
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
